import { Building2, MapPin, Bed, Bath, Maximize, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { formatEuro } from '../utils/format';

const getEstadoStyle = (estado) => {
  switch (estado) {
    case 'Activo':
      return 'bg-sky-300 text-sky-900';
    case 'Ofertado':
      return 'bg-amber-400 text-amber-900';
    case 'Arras':
      return 'bg-orange-500 text-white';
    case 'En cartera':
      return 'bg-[#166534] text-white';
    case 'Reservado':
      return 'bg-red-600 text-white';
    case 'Vendido':
      return 'bg-black text-white';
    case 'Descartado':
      return 'bg-gray-400 text-white';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export const PropertyCard = ({ property }) => {
  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const imageUrl = property.fotos && property.fotos.length > 0
    ? `${BACKEND_URL}/api/photos/${property.fotos[0]}`
    : 'https://images.unsplash.com/photo-1706808849777-96e0d7be3bb7?w=400';

  return (
    <Card 
      data-testid={`property-card-${property.id}`}
      className="overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer group"
      onClick={() => navigate(`/properties/${property.id}`)}
    >
      <div className="relative h-32 overflow-hidden">
        <img
          src={imageUrl}
          alt={property.direccion}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          style={{ imageOrientation: 'from-image' }}
        />
        <span 
          className={`absolute top-2 right-2 shadow-md text-[10px] px-2 py-0.5 rounded-full font-medium ${getEstadoStyle(property.estado)}`}
          data-testid={`property-status-${property.id}`}
        >
          {property.estado || 'Activo'}
        </span>
      </div>
      <div className="p-3 space-y-2">
        <div>
          <div className="flex items-start gap-1 mb-1">
            <MapPin className="text-muted-foreground mt-0.5 flex-shrink-0" size={12} />
            <h3 className="font-semibold text-sm leading-tight line-clamp-1">{property.direccion}</h3>
          </div>
          <div className="flex items-center justify-between">
            {property.ref && (
              <p className="text-xs font-medium text-primary">ID: {property.ref}</p>
            )}
            {property.dias_en_cartera !== null && property.dias_en_cartera !== undefined && (
              <div className="flex items-center gap-0.5 text-xs text-muted-foreground" data-testid={`property-dias-${property.id}`}>
                <Calendar size={12} />
                <span>{property.dias_en_cartera} días</span>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-3">
            {property.superficie > 0 && (
              <div className="flex items-center gap-0.5">
                <Maximize size={12} />
                <span>{property.superficie}m²</span>
              </div>
            )}
            {property.habitaciones > 0 && (
              <div className="flex items-center gap-0.5">
                <Bed size={12} />
                <span>{property.habitaciones}</span>
              </div>
            )}
            {property.banos > 0 && (
              <div className="flex items-center gap-0.5">
                <Bath size={12} />
                <span>{property.banos}</span>
              </div>
            )}
          </div>
          {property.fuente && (
            <span className="font-bold text-green-600">{property.fuente}</span>
          )}
        </div>

        <div className="pt-2 border-t space-y-1">
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground">P. Compra</span>
            <span className="font-semibold text-xs text-primary">{formatEuro(property.precio_compra)}</span>
          </div>
          {property.precio_venta > 0 && (
            <>
              <div className="flex justify-between items-center">
                <span className="text-xs text-muted-foreground">P. Venta</span>
                <span className="font-semibold text-xs">{formatEuro(property.precio_venta)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-muted-foreground">Bº Estimado</span>
                <span className={`font-semibold text-xs ${property.beneficio >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatEuro(property.beneficio)} ({property.porcentaje_beneficio?.toFixed(1) || 0}%)
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  );
};